/* import __COLOCATED_TEMPLATE__ from './ftw-panel.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import type CircuitService from '../services/circuit';
import { ProductRow } from 'simdds/services/state';
import Owner from '@ember/owner';

interface FTWPanelSignature {
  // We don't really have any args, but this gives us something to pass to the constructor
  Args: object;
}
export default class FTWPanel extends Component<FTWPanelSignature> {
  @service declare intl: IntlService;
  @service('aditools-site') declare siteconfig: SiteService;
  @service declare circuit: CircuitService;

  ftwformats = [
    { value: '0', display: 'HEX' },
    { value: '1', display: 'BIN' },
    { value: '2', display: 'DEC' },
  ];
  @tracked selectedFormat = '0';
  @tracked isBinaryKeyPressed = false;
  @tracked isChangeButtonClicked = false;

  constructor(owner: Owner, args: object) {
    super(owner, args);
    const result = this.circuit.uncommittedFtwValue;
    //TODO: When is `uncommittedFtwValue` ever returned to null after it has ever been set?
    if (result) {
      this.circuit.inComponentExceededFtw = true;
    }
  }
  get isHex() {
    return parseInt(this.selectedFormat) === 0;
  }
  get isBinary() {
    return parseInt(this.selectedFormat) === 1;
  }
  get isDecimal() {
    return parseInt(this.selectedFormat) === 2;
  }
  get isExceededFtwInput() {
    return this.circuit.isExceededFtw && this.circuit.inComponentExceededFtw === true;
  }
  get showErrorMessage() {
    return this.isExceededFtwInput || this.circuit.hasCriticalError;
  }
  get exceedErrorMessage() {
    if (this.isExceededFtwInput) {
      const part = this.circuit.partData as ProductRow;
      const ftwValue = this.circuit.uncommittedFtwValue !== null ? this.circuit.uncommittedFtwValue : this.circuit.ftw;
      const realBits = Math.floor(Math.log(ftwValue) / Math.log(2)) + 1;
      const nibbleBits = 4 * (Math.floor(realBits / 4) + 1); // round to 4-bit nibbles
      return this.intl.t('errors.error_ftw_exceed', {
        actual: nibbleBits,
        device: part.name,
        maximum: part.tuningWordWidth,
        ftwinput: ftwValue.toString(16).toUpperCase(),
      });
    } else {
      return '';
    }
  }
  @action onChange(event: Event) {
    const newFormat = (event.target as HTMLSelectElement).value;
    if (newFormat !== this.selectedFormat || newFormat !== '1') {
      const changeButton = document.getElementById('ftw_change_button');
      if (changeButton) {
        changeButton.style.display = 'none';
      }
    }
    this.selectedFormat = newFormat;
  }
  @action ftwChanged(ftwValue: number) {
    const value = ftwValue < 1 || !ftwValue ? 1 : ftwValue;
    this.circuit.uncommittedFtwValue = value;
    const tuningWordWidth = (this.circuit.partData as ProductRow).tuningWordWidth;
    const maxFTWValue = Math.pow(2, tuningWordWidth) - 1;

    if (value > maxFTWValue) {
      this.circuit.inComponentExceededFtw = true;
      this.circuit.ftw = Number.NaN;
    } else {
      this.circuit.inComponentExceededFtw = false;
      this.circuit.ftw = this.circuit.isSystemClockInvalid ? Number.NaN : value;
    }
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    FtwPanel: typeof FTWPanel;
  }
}
